import { useEffect, useState, ReactNode } from "react";
import { sunboundPayProxy } from '@proxies';
import { useSpinnerContext, useSessionContext } from '@contexts';
import { Constants, Enums } from "@shared";
import { Bill, NoBill } from "@components";
import { DashboardLayout } from "@layouts";
import { DateTime } from "luxon";
import currency from "currency.js";
import { useAllowedPaymentTypes } from "../hooks/useAllowedPaymentTypes/useAllowedPaymentTypes";
import type { CurrentBill } from '@app-types';

interface Alert {
    title: string;
    content: ReactNode;
}

interface Tasks {
    currentBill: CurrentBill | null;
}

const Guarantor = () => {

    const { setSpinnerShow } = useSpinnerContext();
    const [tasks, setTasks] = useState<Tasks | null>(null);
    const { session, setSession } = useSessionContext();

    const {
        currentBill = null
    } = tasks || {};
    const { idAccount } = session || {};

    const {
      allowedPaymentTypes,
      isLoading: allowedPaymentTypesIsLoading,
      isError: allowedPaymentTypesIsError
    } = useAllowedPaymentTypes();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        (async () => {

            setSpinnerShow(true);
            try {
                const response = await sunboundPayProxy.refreshSession();
                setSession(response);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, []);

    const {
        paymentPreferences: { hasDefaultPaymentMethod = false, defaultPaymentMethod = null } = {},
        accountPreferences: { isAutoPay = false } = {},
        creditChecked,
        idPayor,
        safeToday
    } = session || {};

    useEffect(() => {
        (async () => {
            if (!idAccount) return;
            setSpinnerShow(true);
            try {
                const responseTasks = await sunboundPayProxy.getGuarantorTasks(idAccount);
                setTasks(responseTasks);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, [idAccount]);


    const { hasDefaultPaymentMethodDisabled = false } = session?.paymentPreferences || {};
    const defaultPaymentMethodInvalid = hasDefaultPaymentMethod && defaultPaymentMethod?.idPaymentType !== undefined && !allowedPaymentTypes.includes(defaultPaymentMethod.idPaymentType);

    const alerts: Alert[] = [];

    if (currentBill) {
        const {
            idProductType,
            invitations
        } = currentBill;

        const invitation = invitations.find(i => i.idPayor === idPayor);
        const failedTransaction = invitation?.payments.find(p => p.idTransactionStatus === Enums.TransactionStatus.declined);
        const { balance, autodrawDate } = invitation || {};
        const todayDay = safeToday ? DateTime.fromISO(safeToday) : DateTime.now();

        if(failedTransaction) {
            const { transactionStatusCode } = failedTransaction;
            const transactionAction = transactionStatusCode
                ? Constants.FailedTransactionsActions.find((action) => action.statusCodes.includes(transactionStatusCode))
                : undefined;
            const title = failedTransaction.transactionStatusReason
                ? `Payment Failed: ${failedTransaction.transactionStatusReason}`
                : `Payment Failed`;
            alerts.push({
                title,
                content: transactionAction?.action || ``
            });
        }

        if (autodrawDate) {
            const isAutopayLate = todayDay.diff(DateTime.fromISO(autodrawDate), `days`).days >= 0;
            if (balance !== undefined && currency(balance).intValue !== 0 && isAutopayLate && isAutoPay) {
                alerts.push({
                    title: `Make a Payment`,
                    content: `Autopay has passed without payment. Make a payment.`
                });
            }
        }

        if (!creditChecked && idProductType === Enums.ProductType.SunboundPay)
            alerts.push({
                title: `Credit Check Pending`,
                content: `We are still confirming your eligibility for Pay Over Time. We'll be in touch within 24 hours with approval.`
            });
    }

    if (hasDefaultPaymentMethodDisabled || (!allowedPaymentTypesIsLoading && !allowedPaymentTypesIsError && defaultPaymentMethodInvalid)) {
        alerts.push({
            title: `Payment Method Disabled`,
            content: <>Please <a className="underline" href="/payment-method-settings">Go to Payment Settings</a> and update your payment method in order to continue making payments.</>
        });
    }

    return (
        <DashboardLayout alerts={alerts} isEmpty={currentBill === null}>
            {currentBill != null && <Bill currentBill={currentBill} />}
            {currentBill === null && <NoBill />}
        </DashboardLayout>
    );
};

export default Guarantor;
